import * as React from 'react';
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { cx } from '../utils';
import type { UnsafeProps } from '../escape-hatches';

export type LinkVariant = 'inline' | 'standalone';
export type LinkIntent = 'default' | 'muted';
export type LinkSize = 'sm' | 'md';

export interface LinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className'>, UnsafeProps {
  /** Layout variant. */
  variant?: LinkVariant;
  /** Color intent. */
  intent?: LinkIntent;
  /** Size — standalone only. */
  size?: LinkSize;
  /** Show external link icon + target="_blank" + rel="noopener noreferrer" */
  external?: boolean;
  /** Show visited state coloring. Off by default (privacy). */
  showVisited?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Arrow-up-right icon for external links.
 * Rendered inline at 0.75em so it scales with surrounding text.
 */
function ExternalIcon() {
  return (
    <svg
      className="j-link-external-icon"
      aria-hidden="true"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3.5 1.5h7v7" />
      <path d="M10.5 1.5L1.5 10.5" />
    </svg>
  );
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    children,
    variant = 'inline',
    intent = 'default',
    size = 'md',
    external,
    showVisited,
    className,
    unsafe_className,
    unsafe_style,
    target,
    rel,
    ...rest
  },
  ref,
) {
  const classes = cx(
    'j-link',
    variant === 'inline' ? 'j-link-inline' : 'j-link-standalone',
    variant === 'standalone' && `j-link-${size}`,
    intent === 'muted' && 'j-link-muted',
    showVisited && 'j-link-visited',
    className,
    unsafe_className,
  );

  const externalTarget = external ? '_blank' : target;
  const externalRel = external ? 'noopener noreferrer' : rel;

  return (
    <a
      {...rest}
      ref={ref}
      className={classes}
      style={unsafe_style}
      target={externalTarget}
      rel={externalRel}
    >
      {children}
      {external && (
        <>
          <ExternalIcon />
          <span
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            (opens in new tab)
          </span>
        </>
      )}
    </a>
  );
});
